/**
 * Community polls — admin moderation surface.
 *
 * Lightweight sentiment surveys (date picks, opinion polls). Distinct from
 * Elections (committee posts with secret ballot). Admin creates polls, edits
 * while draft, closes early, or archives.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BarChart3, Plus, X, Trash2, Archive, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/empty-state';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/format';

type PollStatus = 'draft' | 'active' | 'closed' | 'archived';
type PollAudience = 'all' | 'owners_only' | 'tenants_only' | 'block';

const STATUS_LABEL: Record<PollStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  closed: 'Closed',
  archived: 'Archived'
};
const STATUS_VARIANT: Record<PollStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  draft: 'muted',
  active: 'info',
  closed: 'ok',
  archived: 'muted'
};

const AUDIENCE_LABEL: Record<PollAudience, string> = {
  all: 'All residents',
  owners_only: 'Owners only',
  tenants_only: 'Tenants only',
  block: 'Specific block'
};

interface PollOption {
  id: string;
  poll_id: string;
  label: string;
  sort_order: number;
}

interface PollRow {
  id: string;
  question: string;
  description: string | null;
  status: PollStatus;
  audience: PollAudience;
  audience_block_id: string | null;
  allow_multiple_votes: boolean;
  show_results_live: boolean;
  opens_at: string;
  closes_at: string | null;
  results_revealed_at: string | null;
  created_at: string;
  options: PollOption[];
  _count?: { votes: number };
}

interface PollDetail extends PollRow {
  my_vote_option_ids: string[];
  results_visible: boolean;
  option_counts: Record<string, number>;
  total_votes: number;
}

const OptionSchema = z.object({ label: z.string().trim().min(1, 'Required').max(200) });
const CreateSchema = z.object({
  question: z.string().trim().min(3).max(500),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  audience: z.enum(['all', 'owners_only', 'tenants_only', 'block']),
  allow_multiple_votes: z.boolean(),
  show_results_live: z.boolean(),
  closes_at: z.string().optional().or(z.literal('')),
  options: z.array(OptionSchema).min(2).max(20)
});
type CreateForm = z.infer<typeof CreateSchema>;
const DEFAULTS: CreateForm = {
  question: '',
  description: '',
  audience: 'all',
  allow_multiple_votes: false,
  show_results_live: true,
  closes_at: '',
  options: [{ label: '' }, { label: '' }]
};

export function CommunityPollsPage(): JSX.Element {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PollStatus | 'all_statuses'>('all_statuses');
  const [resultsTarget, setResultsTarget] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState<PollRow | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<PollRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PollRow | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULTS,
    mode: 'onChange'
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'options' });

  const listQuery = useQuery({
    queryKey: ['community-polls', statusFilter],
    queryFn: () =>
      get<{ items: PollRow[]; next_cursor: string | null }>('/community-polls', {
        params: {
          limit: 50,
          ...(statusFilter !== 'all_statuses' ? { status: statusFilter } : {})
        }
      })
  });
  const items = listQuery.data?.items ?? [];

  const detailQuery = useQuery({
    queryKey: ['community-polls', resultsTarget],
    enabled: Boolean(resultsTarget),
    queryFn: () => get<PollDetail>(`/community-polls/${resultsTarget}`)
  });

  const create = useMutation({
    mutationFn: (v: CreateForm) => {
      const body: Record<string, unknown> = {
        question: v.question,
        audience: v.audience,
        allow_multiple_votes: v.allow_multiple_votes,
        show_results_live: v.show_results_live,
        options: v.options.map((o) => ({ label: o.label.trim() }))
      };
      if (v.description?.trim()) body.description = v.description.trim();
      if (v.closes_at) body.closes_at = new Date(v.closes_at).toISOString();
      return post<PollRow>('/community-polls', body);
    },
    onSuccess: () => {
      toast.success('Poll created');
      setCreateOpen(false);
      form.reset(DEFAULTS);
      void qc.invalidateQueries({ queryKey: ['community-polls'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const closeMut = useMutation({
    mutationFn: (id: string) => post<PollRow>(`/community-polls/${id}/close`, {}),
    onSuccess: () => {
      toast.success('Poll closed');
      setConfirmClose(null);
      void qc.invalidateQueries({ queryKey: ['community-polls'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => post<PollRow>(`/community-polls/${id}/archive`, {}),
    onSuccess: () => {
      toast.success('Poll archived');
      setConfirmArchive(null);
      void qc.invalidateQueries({ queryKey: ['community-polls'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del<void>(`/community-polls/${id}`),
    onSuccess: () => {
      toast.success('Poll deleted');
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: ['community-polls'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const totalVotes = items.reduce((acc, p) => acc + (p._count?.votes ?? 0), 0);
  const activeCount = items.filter((p) => p.status === 'active').length;
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const closedRecent = items.filter(
    (p) => p.status === 'closed' && new Date(p.created_at).getTime() >= thirtyDaysAgo
  ).length;

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Community polls"
        description="Lightweight sentiment surveys (Holi date pick, swimming-pool hours, etc). Separate from Elections — no secret ballot, just a tally."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Create poll
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        {[
          ['Total', items.length],
          ['Active', activeCount],
          ['Closed (30d)', closedRecent],
          ['Total votes', totalVotes]
        ].map(([label, n]) => (
          <Card key={String(label)}>
            <CardContent className="p-vt-4">
              <div className="text-vt-12 text-vt-text-muted">{label}</div>
              <div className="mt-1 text-vt-24 font-semibold text-vt-text">{n}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-vt-2">
        <Label className="text-vt-12 text-vt-text-muted">Status</Label>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PollStatus | 'all_statuses')}
          className="max-w-[200px]"
        >
          <option value="all_statuses">All</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="archived">Archived</option>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="space-y-2 p-vt-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<BarChart3 className="h-6 w-6" />}
              title="No polls yet"
              description="Create a poll to gather quick feedback from residents."
              action={
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" /> Create poll
                </Button>
              }
            />
          ) : (
            <div className="divide-y divide-vt-border">
              {items.map((p) => {
                const daysLeft = p.closes_at
                  ? Math.ceil((new Date(p.closes_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
                  : null;
                return (
                  <div key={p.id} className="flex items-start gap-vt-3 p-vt-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-vt-2">
                        <div className="truncate text-vt-15 font-semibold text-vt-text">{p.question}</div>
                        <Badge variant={STATUS_VARIANT[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                      </div>
                      <div className="mt-vt-1 text-vt-12 text-vt-text-muted">
                        {AUDIENCE_LABEL[p.audience]} · {p.options.length} options ·{' '}
                        {p._count?.votes ?? 0} votes
                        {p.allow_multiple_votes ? ' · multi-vote' : ''}
                        {p.closes_at
                          ? ` · closes ${formatDateTime(p.closes_at)}${
                              daysLeft !== null && daysLeft > 0 ? ` (${daysLeft}d left)` : ''
                            }`
                          : ' · no deadline'}
                      </div>
                      {p.description ? (
                        <p className="mt-vt-2 line-clamp-2 text-vt-13 text-vt-text-soft">{p.description}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-vt-2">
                      <Button variant="ghost" size="sm" onClick={() => setResultsTarget(p.id)}>
                        Results
                      </Button>
                      {p.status === 'active' ? (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmClose(p)}>
                          <Lock className="h-4 w-4" /> Close
                        </Button>
                      ) : null}
                      {p.status === 'closed' ? (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmArchive(p)}>
                          <Archive className="h-4 w-4" /> Archive
                        </Button>
                      ) : null}
                      {p.status === 'draft' ? (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(p)}>
                          <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create poll</DialogTitle>
            <DialogDescription>
              Add a question and at least two options. Polls open immediately by default.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit((v) => create.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div>
              <Label htmlFor="question">Question</Label>
              <Input id="question" {...form.register('question')} maxLength={500} />
              {form.formState.errors.question ? (
                <p className="mt-vt-1 text-vt-12 text-vt-danger">
                  {form.formState.errors.question.message}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea id="description" rows={2} {...form.register('description')} maxLength={2000} />
            </div>
            <div className="grid grid-cols-2 gap-vt-3">
              <div>
                <Label htmlFor="audience">Audience</Label>
                <Select id="audience" {...form.register('audience')}>
                  <option value="all">All residents</option>
                  <option value="owners_only">Owners only</option>
                  <option value="tenants_only">Tenants only</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="closes_at">Closes at (optional)</Label>
                <Input id="closes_at" type="datetime-local" {...form.register('closes_at')} />
              </div>
            </div>
            <div className="flex items-center gap-vt-4">
              <label className="flex items-center gap-vt-2 text-vt-13">
                <input type="checkbox" {...form.register('allow_multiple_votes')} />
                Allow multiple options per voter
              </label>
              <label className="flex items-center gap-vt-2 text-vt-13">
                <input type="checkbox" {...form.register('show_results_live')} />
                Show results live
              </label>
            </div>
            <div>
              <Label>Options</Label>
              <div className="flex flex-col gap-vt-2">
                {fields.map((f, idx) => (
                  <div key={f.id} className="flex items-center gap-vt-2">
                    <Input
                      placeholder={`Option ${idx + 1}`}
                      {...form.register(`options.${idx}.label` as const)}
                      maxLength={200}
                    />
                    {fields.length > 2 ? (
                      <button
                        type="button"
                        aria-label="Remove option"
                        className="text-vt-text-muted hover:text-vt-danger"
                        onClick={() => remove(idx)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                ))}
                {fields.length < 20 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => append({ label: '' })}
                  >
                    <Plus className="h-4 w-4" /> Add option
                  </Button>
                ) : null}
              </div>
              {form.formState.errors.options ? (
                <p className="mt-vt-1 text-vt-12 text-vt-danger">
                  Each option must have a non-empty label.
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create poll'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Results dialog */}
      <Dialog open={Boolean(resultsTarget)} onOpenChange={(o) => !o && setResultsTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detailQuery.data?.question ?? 'Poll results'}</DialogTitle>
            <DialogDescription>
              {detailQuery.data
                ? `${detailQuery.data.total_votes} votes · ${STATUS_LABEL[detailQuery.data.status]}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : detailQuery.data ? (
            <ResultsBars detail={detailQuery.data} />
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResultsTarget(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmClose)}
        onOpenChange={(o) => !o && setConfirmClose(null)}
        title="Close poll?"
        description={`This will lock voting on "${confirmClose?.question ?? ''}" and reveal results to all residents.`}
        confirmLabel="Close poll"
        onConfirm={() => { if (confirmClose) closeMut.mutate(confirmClose.id); }}
      />
      <ConfirmDialog
        open={Boolean(confirmArchive)}
        onOpenChange={(o) => !o && setConfirmArchive(null)}
        title="Archive poll?"
        description={`Move "${confirmArchive?.question ?? ''}" to the archive. Residents will no longer see it on their feed.`}
        confirmLabel="Archive"
        onConfirm={() => { if (confirmArchive) archiveMut.mutate(confirmArchive.id); }}
      />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete draft poll?"
        description={`Permanently delete "${confirmDelete?.question ?? ''}". This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => { if (confirmDelete) deleteMut.mutate(confirmDelete.id); }}
      />
    </div>
  );
}

interface ResultsBarsProps {
  detail: PollDetail;
}

function ResultsBars({ detail }: ResultsBarsProps): JSX.Element {
  if (!detail.results_visible) {
    return (
      <div className="rounded-vt-sm border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-13 text-vt-text-muted">
        Results are hidden until this poll closes.
      </div>
    );
  }
  const total = detail.total_votes;
  const sorted = [...detail.options].sort((a, b) => {
    const cb = detail.option_counts[b.id] ?? 0;
    const ca = detail.option_counts[a.id] ?? 0;
    return cb - ca;
  });
  return (
    <div className="flex flex-col gap-vt-3">
      {sorted.map((o) => {
        const count = detail.option_counts[o.id] ?? 0;
        const pct = total > 0 ? Math.round((count * 100) / total) : 0;
        const mine = detail.my_vote_option_ids.includes(o.id);
        return (
          <div key={o.id}>
            <div className="flex items-center justify-between text-vt-13">
              <div className="flex items-center gap-vt-2 text-vt-text">
                {mine ? <CheckCircle2 className="h-4 w-4 text-vt-primary" /> : null}
                <span>{o.label}</span>
              </div>
              <span className="text-vt-text-muted">
                {count} ({pct}%)
              </span>
            </div>
            <div className="mt-vt-1 h-2 w-full rounded-full bg-vt-surface-2">
              <div
                className="h-2 rounded-full bg-vt-primary transition-all duration-vt"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
